<template>
    <div class="error-msg-wrap">
        <div class="form-wrapper slide-up add-board">
            <div class="form-close">
                <button type="button" class="form-close-x" title="Close" @click="$emit('close')">&times;</button>
            </div>
            <h3>Name</h3>
            <input id="add-board-name" type="text" v-model="formData.Name" @input="setSlug()" @keyup.enter="submit()" />

            <!-- Starting columns: the company's default template (or the standard columns when it has
                 none), or another board template -->
            <template v-if="otherTemplates.length || canManageTemplates">
                <h3>Start from</h3>
                <div class="add-board-templates">
                    <label :class="formData.TemplateID ? 'add-board-template' : 'add-board-template on'">
                        <input type="radio" name="add-board-template" :value="null" v-model="formData.TemplateID" />
                        <span class="add-board-template-text">
                            <span class="add-board-template-name">Default</span>
                            <span v-if="defaultTemplate && defaultTemplate.Description" class="add-board-template-desc">{{ defaultTemplate.Description }}</span>
                            <span class="add-board-template-cols">
                                <span v-for="(col, ci) in defaultColumns" :key="ci" class="add-board-template-chip" :style="'background:' + col.color">{{ col.name }}</span>
                            </span>
                            <span v-if="defaultTemplate && defaultTemplate.Tags.length" class="add-board-template-cols">
                                <span v-for="(tag, ti) in defaultTemplate.Tags" :key="ti" class="add-board-template-tag" :style="'background:' + tag.color">{{ tag.name }}</span>
                            </span>
                        </span>
                    </label>
                    <label v-for="t in otherTemplates" :key="t.ID" :class="formData.TemplateID == t.ID ? 'add-board-template on' : 'add-board-template'">
                        <input type="radio" name="add-board-template" :value="t.ID" v-model="formData.TemplateID" />
                        <span class="add-board-template-text">
                            <span class="add-board-template-name">{{ t.Name }}</span>
                            <span v-if="t.Description" class="add-board-template-desc">{{ t.Description }}</span>
                            <span class="add-board-template-cols">
                                <span v-for="(col, ci) in t.Columns" :key="ci" class="add-board-template-chip" :style="'background:' + col.color">{{ col.name }}</span>
                            </span>
                            <span v-if="t.Tags.length" class="add-board-template-cols">
                                <span v-for="(tag, ti) in t.Tags" :key="ti" class="add-board-template-tag" :style="'background:' + tag.color">{{ tag.name }}</span>
                            </span>
                        </span>
                    </label>
                </div>
                <div v-if="canManageTemplates" class="add-board-manage"><a @click.prevent="manageTemplates()">Manage templates</a></div>
            </template>

            <input :class="disableButton ? 'disable-button' : ''" type="submit" value="Submit" @click="submit()" />
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

export default defineComponent({
    name: 'AddCompanyBoard',
    emits: [ 'close' ],
    data() {
        return {
            disableButton: false,
            formData: {
                Name: '',
                Slug: '',
                TemplateID: null as any
            },
            templates: [] as any
        }
    },
    computed: {
        // Same rule as the API: Playbook Admins and super admins
        canManageTemplates(): boolean {
            return this.$store.state.user.role == 'Admin' || !!this.$store.state.user.superAdmin;
        },
        defaultTemplate(): any {
            return this.templates.find((t: any) => t.IsDefault) || null;
        },
        // With no default template, the Default option uses the API's coded columns (companyBoards.ts DEFAULT_BOARD_COLUMNS)
        defaultColumns(): any[] {
            return this.defaultTemplate ? this.defaultTemplate.Columns : [
                { name: 'To Do', color: '#6B7075' },
                { name: 'In Progress', color: '#E8912F' },
                { name: 'Done', color: '#3E8E4E' }
            ];
        },
        otherTemplates(): any[] {
            return this.templates.filter((t: any) => !t.IsDefault);
        }
    },
    async created() {
        await axios.get(`${this.$store.state.apiUrl}/api/companyboardtemplates`)
            .then(resp => {
                this.templates = resp.data.map((t: any) => ({
                    ...t,
                    Columns: t.ColumnsJSON ? JSON.parse(t.ColumnsJSON) : [],
                    Tags: t.TagsJSON ? JSON.parse(t.TagsJSON) : []
                }));
            })
            .catch(e => {
                console.log(e);
            })
    },
    mounted() {
        document.getElementById('add-board-name')?.focus();
    },
    methods: {
        async submit() {
            if(!this.formData.Name || !this.formData.Slug || this.disableButton) {
                return;
            }

            this.disableButton = true;

            await axios.post(`${this.$store.state.apiUrl}/api/companyboards`, this.formData)
                .then(() => {
                    this.$emit('close');
                    this.$router.push('/deck/' + this.formData.Slug)
                })
                .catch(e => {
                    console.log(e);
                    alert(e?.response?.data || 'There was an error')
                })

            this.disableButton = false;
        },
        setSlug() {
            this.formData.Slug = this.formData.Name.toLowerCase().replace(/[^a-zA-Z0-9- ]/g, '').replace(/ /g, '-') + '-' + uuidv4();
        },
        manageTemplates() {
            this.$emit('close');
            this.$router.push('/board-templates');
        }
    }
});
</script>

<style scoped>
/* The popup is capped by .error-msg-wrap .form-wrapper; as a flex column, only the template list
   scrolls, taking whatever height is left */
.add-board { display: flex; flex-direction: column; overflow: hidden; }
.add-board > *:not(.form-close) { flex-shrink: 0; }
.add-board .add-board-templates { flex: 1 1 auto; min-height: 90px; overflow: auto; display: flex; flex-direction: column; gap: 8px; margin-bottom: 10px; }
.add-board-template { display: flex; align-items: flex-start; gap: 10px; border: 1px solid var(--mid-gray); border-radius: 8px; padding: 10px 12px; cursor: pointer; }
.add-board-template.on { border-color: var(--primary-color); background: #fdf3f3; }
.add-board-template input[type=radio] { width: auto; margin: 3px 0 0; flex-shrink: 0; }
.add-board-template-text { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
.add-board-template-name { font-weight: 700; font-size: 14px; }
.add-board-template-desc { font-size: 12.5px; color: #8a8580; }
.add-board-template-cols { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 3px; }
.add-board-template-chip { font-size: 11px; font-weight: 600; color: #2c2a29; border-radius: 4px; padding: 1px 7px; border: 1px solid rgba(0,0,0,.12); }
.add-board-template-tag { font-size: 11px; font-weight: 700; color: white; border-radius: 3px; padding: 1px 6px; }
.add-board-manage { margin-bottom: 10px; font-size: 13px; }
.add-board-manage a { text-decoration: underline; cursor: pointer; }
</style>
